import React from 'react';
import ImgHappy from '../../../../assets/happy-emoji.png';
import ImgUnHappy from '../../../../assets/unhappy-emoji.png';
import CheckCircleFilled from '@ant-design/icons/lib/icons/CheckCircleFilled';
import CloseCircleFilled from '@ant-design/icons/lib/icons/CloseCircleFilled';
import { Button, Radio, RadioChangeEvent } from 'antd';
import { useStore } from '../../store';

interface IQuizOption {
  indicator?: string;
  title: any;
  explanation: any;
  isCorrect: boolean;
}

interface IFinalQuizProps {
  question: any;
  options: any;
  quizIndex: number;
  quizCount: number;
  onSubmit?: (selected: IQuizOption) => void;
  onNext?: (isLast: boolean) => void;
  onPrev?: () => void;
}
const RESULTS = {
  CORRECT: 'correct',
  WRONG: 'wrong',
};
const INDICATORS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const FinalQuiz: React.FunctionComponent<IFinalQuizProps> = (props) => {
  const { question, options, onSubmit, onNext, onPrev, quizIndex, quizCount } =
    props;

  const { language } = useStore();
  const settings =
    language === 'en'
      ? {
          headerLabel: 'Quiz',
          titleLabel: 'Select the correct answer',
          correctLabel: 'Correct',
          wrongLabel: 'Wrong!',
          tryLabel: 'Try again',
          submitLabel: 'Submit',
          lastLabel: 'Previous Question',
          nextLabel: 'Next',
          checkResult: 'Check Results',
        }
      : language === 'sc'
      ? {
          headerLabel: '小测验',
          titleLabel: '选择正确的答案',
          correctLabel: '正确答案',
          wrongLabel: '错误答案!',
          tryLabel: '再试试',
          submitLabel: '提交',
          lastLabel: '上一题',
          nextLabel: '下一题',
          checkResult: '查看成绩',
        }
      : {
          headerLabel: '小測驗',
          titleLabel: '選擇正確的答案',
          correctLabel: '正確答案',
          wrongLabel: '錯誤答案!',
          tryLabel: '再試試',
          submitLabel: '提交',
          lastLabel: '上一題',
          nextLabel: '下一道題',
          checkResult: '查看成績',
        };
  options.forEach((item: any, index: number) => {
    item.indicator = INDICATORS[index];
  });
  const [state, setState] = React.useState<{
    submitted: boolean;
    selected: IQuizOption | undefined;
    selectedValue: string;
  }>({
    submitted: false,
    selected: undefined,
    selectedValue: '',
  });

  const getAnswerStyle = (itm: string) =>
    !state.submitted || itm !== state.selected?.indicator
      ? ''
      : state.selected.isCorrect
      ? RESULTS.CORRECT
      : RESULTS.WRONG;
  const onSelected = (e: RadioChangeEvent) => {
    if (state.submitted) {
      return;
    }
    setState({
      ...state,
      selectedValue: e.target.value,
      selected: options.find(
        (option: any) => option.indicator === e.target.value
      ),
    });
  };
  // eslint-disable-next-line no-unused-vars
  const last = () => {
    onPrev && onPrev();
  };
  const submit = () => {
    if (!state.selected) {
      return;
    }
    setState({
      ...state,
      submitted: true,
    });
    if (typeof onSubmit === 'function') {
      onSubmit(state.selected);
    }
  };
  React.useEffect(() => {
    if (state.submitted) {
      document?.querySelector('.final-quiz-container')?.scrollTo({
        top: 0,
        left: 0,
      });
    }
  }, [state.submitted]);

  const next = () => {
    onNext && onNext(quizIndex === quizCount);

    setState({
      ...state,
      submitted: false,
    });
  };
  return (
    <div id={`q-${quizIndex}`} className="quiz-section">
      <div className="quiz-section-body">
        <div className="quiz-index">
          {quizIndex}/{quizCount}
        </div>
        <div className="quiz-title">{settings?.titleLabel}</div>
        <div
          className="quiz-question"
          dangerouslySetInnerHTML={{
            __html: `${quizIndex}. ${question[language]} `,
          }}
        />
        <div className="quiz-answers">
          <Radio.Group onChange={onSelected} value={state.selectedValue}>
            {options.map((option: any, index: number) => (
              <div
                key={index}
                className={`quiz-answers-item ${getAnswerStyle(
                  option.indicator || ''
                )}`}
              >
                <Radio value={option.indicator}>
                  {getAnswerStyle(option.indicator || '') ===
                  RESULTS.CORRECT ? (
                    <CheckCircleFilled />
                  ) : null}
                  {getAnswerStyle(option.indicator || '') === RESULTS.WRONG ? (
                    <CloseCircleFilled />
                  ) : null}
                  {option.indicator}. {option.title[language]}
                </Radio>
              </div>
            ))}
          </Radio.Group>
        </div>
        <div className="quiz-button-section">
          <Button
            disabled={state.selectedValue === ''}
            onClick={submit}
            className={`quiz-submit-button ${state.submitted ? 'hide' : ''}`}
            type="text"
          >
            <span>{settings?.submitLabel}</span>
          </Button>
        </div>
        <div className={!state.submitted ? 'hide' : ''}>
          <div
            className={`quiz-result-area correct ${
              state.selected?.isCorrect ? 'correct' : 'wrong'
            }`}
          >
            <img
              src={state.selected?.isCorrect ? ImgHappy : ImgUnHappy}
              alt=""
            />
            <div className="text">
              {state.selected?.isCorrect
                ? settings?.correctLabel
                : settings?.wrongLabel}
            </div>
            <p
              dangerouslySetInnerHTML={{
                __html: state.selected?.explanation[language] || '',
              }}
            />
            <Button
              onClick={next}
              className="quiz-try-again-button"
              type="link"
            >
              {quizIndex === quizCount ? (
                <span>{settings.checkResult}</span>
              ) : (
                <span>{settings.nextLabel}</span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

FinalQuiz.defaultProps = {
  onSubmit: undefined,
  onNext: undefined,
  onPrev: undefined,
};

export default FinalQuiz;
